import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../context/AuthContext";
import "../solutions.css";
import LaserBackground from "./LaserBackground";

const functions = getFunctions();
const solutionsChat = httpsCallable(functions, "solutionsChat");
const CHAT_LIMIT = 7;
const STARTING_MESSAGE = {
  role: "assistant",
  content:
    "What are you struggling with when using AI?\n\nBe specific. Give me a real example.",
};

function getSolutionsSessionId() {
  const existing = localStorage.getItem("solutions_chat_session_id");
  if (existing) return existing;
  const id = `solutions-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  localStorage.setItem("solutions_chat_session_id", id);
  return id;
}

function loadSavedMessages() {
  try {
    const raw = localStorage.getItem("solutions_chat_history");
    if (!raw) return [STARTING_MESSAGE];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return [STARTING_MESSAGE];
    return parsed.filter((m) => m && (m.role === "user" || m.role === "assistant") && typeof m.content === "string");
  } catch {
    return [STARTING_MESSAGE];
  }
}

const modules = [
  {
    num: "01",
    title: "The Brief",
    summary: "Most AI failures start before the first word is typed.",
    detail:
      "Learn to give AI the background, constraints, and objective it needs to perform — the way a skilled director briefs a team. Context isn't optional. It's the entire job.",
  },
  {
    num: "02",
    title: "The Directive",
    summary: "Output arrived. It's close. It's wrong.",
    detail:
      "Learn to steer, push back, and redirect in real time without losing your thread or starting over. The difference between an operator and a passenger is what happens after the first response.",
  },
  {
    num: "03",
    title: "The Stack",
    summary: "Single prompts have a ceiling.",
    detail:
      "Chain tasks, tools, and outputs into repeatable workflows that produce consistent, scalable results. One well-designed stack replaces hours of back-and-forth, indefinitely.",
  },
  {
    num: "04",
    title: "The Review",
    summary: "Knowing when it's good enough is its own skill.",
    detail:
      "AI output can be confidently wrong. Learn the review patterns that catch what matters — and the discipline to ship when it's right — without second-guessing every line.",
  },
];

const Solutions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [pageIn, setPageIn] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [showModules, setShowModules] = useState(false);
  const [showCta, setShowCta] = useState(false);
  const [chatMessages, setChatMessages] = useState(loadSavedMessages);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [showSignupCta, setShowSignupCta] = useState(false);
  const sessionId = useRef(getSolutionsSessionId());

  useEffect(() => {
    document.body.classList.add("mirror-body");
    const t1 = setTimeout(() => setPageIn(true), 60);
    const t2 = setTimeout(() => setShowHeader(true), 300);
    const t3 = setTimeout(() => setShowModules(true), 800);
    const t4 = setTimeout(() => setShowCta(true), 800 + modules.length * 160 + 500);
    return () => {
      document.body.classList.remove("mirror-body");
      [t1, t2, t3, t4].forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("solutions_chat_history", JSON.stringify(chatMessages));
    const userCount = chatMessages.filter((m) => m.role === "user").length;
    setShowSignupCta(userCount >= CHAT_LIMIT);
  }, [chatMessages]);

  async function sendChatMessage() {
    if (!chatInput.trim() || chatLoading || showSignupCta) return;

    const userMessage = { role: "user", content: chatInput.trim() };
    const newMessages = [...chatMessages, userMessage];
    setChatMessages(newMessages);
    setChatInput("");
    setChatLoading(true);

    try {
      const result = await solutionsChat({
        sessionId: sessionId.current,
        messages: newMessages,
      });

      const reply = result.data?.reply || "";
      const cta = result.data?.cta === true;

      if (reply) {
        setChatMessages((prev) => [...prev, { role: "assistant", content: reply }]);
      }

      if (cta) setShowSignupCta(true);
    } catch (err) {
      console.error("solutionsChat error:", err);
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Something went wrong. Try again in a moment." },
      ]);
    } finally {
      setChatLoading(false);
    }
  }

  function onChatKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  }

  return (
    <div className={`solutions-page ${pageIn ? "solutions-page-in" : ""}`}>
      <LaserBackground />

      <Link to="/discover" className="mirror-back-nav">← The Assessment</Link>

      <div className="solutions-content">

        {/* Header */}
        <div className={`solutions-header ${showHeader ? "solutions-header-in" : ""}`}>
          <div className="solutions-eyebrow">THE OPERATOR METHOD</div>
          <h2 className="solutions-headline">
            Four skills.<br />
            One framework.<br />
            Measurable output from day one.
          </h2>
          <div className="solutions-rule" />
          <p className="solutions-subhead">
            This is what separates the professionals getting extraordinary results
            from everyone else using the exact same tools.
          </p>
        </div>

        {/* Module cards */}
        <div className="sol-chat-block">
          <div className="sol-chat-label">LIVE DEMO CHAT</div>

          <div className="sol-chat-window">
            {chatMessages.map((msg, i) => (
              <div key={i} className={`sol-chat-row ${msg.role === "user" ? "sol-chat-user" : "sol-chat-ai"}`}>
                <div className="sol-chat-bubble">
                  {msg.content.split("\n").map((line, j, arr) => (
                    <span key={j}>
                      {line}
                      {j < arr.length - 1 && <br />}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {chatLoading && <div className="sol-chat-loading">Thinking...</div>}
          </div>

          {!showSignupCta ? (
            <div className="sol-chat-input-row">
              <textarea
                id="solutions-chat-input"
                name="solutions-chat-input"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={onChatKeyDown}
                placeholder="Type your answer..."
                rows={2}
                disabled={chatLoading}
                className="sol-chat-input"
              />
              <button onClick={sendChatMessage} disabled={chatLoading || !chatInput.trim()} className="sol-chat-send">
                Send
              </button>
            </div>
          ) : (
            <div className="sol-signup-cta">
              {!currentUser ? (
                <>
                  <p>You just learned your first technique.</p>
                  <p>Want to continue? Sign up to access the full course.</p>
                  <button onClick={() => navigate("/signup", { state: { backgroundLocation: location } })}>Continue Learning →</button>
                </>
              ) : (
                <>
                  <p>You are already signed in.</p>
                  <p>Continue directly to your course.</p>
                  <button onClick={() => navigate('/v2-course')}>Go to Course →</button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Module cards */}
        <div className="solutions-modules">
          {modules.map((mod, i) => (
            <div
              key={i}
              className={`sol-module ${showModules ? "sol-module-visible" : ""}`}
              style={{ animationDelay: showModules ? `${i * 160}ms` : "0ms" }}
            >
              <div className="sol-module-num">{mod.num}</div>
              <div className="sol-module-body">
                <div className="sol-module-title">{mod.title}</div>
                <div className="sol-module-summary">{mod.summary}</div>
                <div className="sol-module-detail">{mod.detail}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Two-path CTA */}
        <div className={`sol-cta-block ${showCta ? "sol-cta-active" : ""}`}>
          <div className="sol-cta-label">Choose your path forward.</div>
          <div className="sol-cta-split">

            <button className="sol-cta-primary" onClick={() => currentUser ? navigate('/v2-course') : navigate("/signup", { state: { backgroundLocation: location } })}>
              <div className="sol-cta-glow" />
              <span className="sol-cta-inner">
                <span className="sol-cta-title">{currentUser ? 'Go to Course' : 'Build It With Our Team'}</span>
                <span className="sol-cta-sub">
                  {currentUser
                    ? 'You are signed in. Continue directly into your learning session.'
                    : 'We apply The Operator Method to your actual workflow — no courseware, just results'}
                </span>
              </span>
              <span className="sol-arrow">→</span>
            </button>

            <div className="sol-divider">or</div>

            <button className="sol-cta-secondary" onClick={() => navigate("/pricing")}>
              <span className="sol-cta-inner">
                <span className="sol-cta-title">Take Me Through It Myself</span>
                <span className="sol-cta-sub">
                  Self-guided curriculum — structured modules, real exercises, your pace
                </span>
              </span>
              <span className="sol-arrow-dim">→</span>
            </button>

          </div>
          <div className="sol-footnote">No commitment on either path.</div>
        </div>

      </div>
    </div>
  );
};

export default Solutions;
